import { z } from 'zod';
import { ATTENDANCE_CALC_MODES } from './enums.js';
import { isValidTimeZone } from './time.js';

/**
 * Company-wide configurable rules. Nothing that is a business rule is hard-coded:
 * it is a setting here, with a default, a validator and an audit trail on change.
 * Settings that must stay historically accurate (reporting periods, schedules,
 * time zones) are stored as effective-dated records instead — see ARCHITECTURE.md.
 */

const tz = z.string().refine(isValidTimeZone, 'Must be a valid IANA time zone, e.g. Asia/Beirut');

export const SETTING_DEFINITIONS = {
  'company.name': {
    group: 'Company',
    label: 'Company name',
    description: 'Shown in the header, emails and reports.',
    schema: z.string().trim().min(1).max(120),
    default: 'My Company',
  },
  'company.default_language': {
    group: 'Company',
    label: 'Default language',
    description: 'Language of the app for people who have not chosen one (everyone can switch between English and Arabic at the top of the screen).',
    schema: z.enum(['en', 'ar']),
    default: 'en',
  },
  'company.timezone': {
    group: 'Company',
    label: 'Company time zone',
    description:
      'Used when an administrator chooses to view times in "company time" instead of each employee\'s local time, and for company-level jobs such as month-end close.',
    schema: tz,
    default: 'UTC',
  },
  'company.logo': {
    group: 'Company',
    label: 'Company logo',
    description: 'Shown on the sign-in page and in the menu. PNG, JPG or WebP up to 500 KB. Empty = the built-in logo.',
    schema: z
      .string()
      .max(700_000, 'The image is too large (max 500 KB)')
      .refine((v) => v === '' || /^data:image\/(png|jpeg|webp);base64,[A-Za-z0-9+/=]+$/.test(v), 'Use a PNG, JPG or WebP image'),
    default: '',
  },
  'org.managers_see_indirect_reports': {
    group: 'Organisation',
    label: 'Managers see indirect reports',
    description: 'If on, a manager can also see employees who report to their direct reports.',
    schema: z.boolean(),
    default: false,
  },
  'attendance.calc_mode': {
    group: 'Attendance',
    label: 'Daily worked-time calculation',
    description:
      'first_last = first sign-in to last sign-out. sum_intervals = sum of each sign-in/sign-out pair. Can be overridden per work schedule or per employee.',
    schema: z.enum(ATTENDANCE_CALC_MODES),
    default: 'first_last',
  },
  'attendance.deduct_break': {
    group: 'Attendance',
    label: 'Subtract the scheduled break',
    description: 'With "first sign-in → last sign-out", subtract the break defined in the work schedule from the worked time.',
    schema: z.boolean(),
    default: true,
  },
  'attendance.web_clock_in': {
    group: 'Attendance',
    label: 'Who may clock in with the app button',
    description:
      'everyone = anyone can press Clock in/out in the app. non_office = only remote/hybrid staff; office staff must use the fingerprint machine or the door QR code. nobody = app button disabled.',
    schema: z.enum(['everyone', 'non_office', 'nobody']),
    default: 'everyone',
  },
  'attendance.computer_hours_count': {
    group: 'Attendance',
    label: 'Hours from computer activity count',
    description:
      'For people whose hours come from the desktop agent (e.g. the remote engineer). active = time the computer was in use (idle excluded). active_no_unproductive = the same without unproductive apps/websites. productive = productive time only. tracked = everything recorded, idle included.',
    schema: z.enum(['active', 'active_no_unproductive', 'productive', 'tracked']),
    default: 'active',
  },
  'attendance.tracking_start_date': {
    group: 'Attendance',
    label: 'Attendance tracking start date',
    description:
      'The day this system started recording attendance. Earlier days without punches show as "Before tracking" instead of "Absent". Set it to your go-live day.',
    schema: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD'),
    default: '2000-01-01',
  },
  'attendance.day_start_hour': {
    group: 'Attendance',
    label: 'Working day starts at (hour)',
    description: 'Punches before this hour (local time) count towards the previous day — for night shifts. 4 = 04:00.',
    schema: z.number().int().min(0).max(12),
    default: 4,
  },
  'attendance.missing_out_after_hours': {
    group: 'Attendance',
    label: 'Flag missing clock-out after (hours)',
    description: 'Hours after the scheduled end of the day with no clock-out before it is flagged as a missing clock-out.',
    schema: z.number().int().min(1).max(12),
    default: 4,
  },
  'attendance.notify_missing_clock_out': {
    group: 'Attendance',
    label: 'Notify about missing clock-outs',
    description: 'The employee and administrators are notified once per day with a missing clock-out.',
    schema: z.boolean(),
    default: true,
  },
  'attendance.qr_valid_seconds': {
    group: 'Attendance',
    label: 'Door QR code validity (seconds)',
    description: 'The door QR code changes every 30 seconds; a scanned code is accepted for this long.',
    schema: z.number().int().min(30).max(300),
    default: 90,
  },
  'attendance.min_punch_gap_seconds': {
    group: 'Attendance',
    label: 'Ignore repeated punches within (seconds)',
    description: 'A second identical punch within this time (e.g. finger scanned twice) is ignored.',
    schema: z.number().int().min(0).max(600),
    default: 60,
  },
  'attendance.repeat_punch_minutes': {
    group: 'Attendance',
    label: 'Treat a punch as a repeat within (minutes)',
    description:
      'For machines without In/Out keys: a punch within this many minutes of the previous one keeps the same direction instead of flipping in↔out (e.g. someone scans the fingerprint and then the door QR). 0 = always alternate.',
    schema: z.number().int().min(0).max(120),
    default: 10,
  },
  'periods.start_day': {
    group: 'Reporting periods',
    label: 'Reporting period starts on day',
    description:
      'Day of the month each reporting period starts (25 = 25th → 24th of the next month; 1 = calendar month). A change applies from the next period that has not started yet — closed and current periods keep their dates.',
    schema: z.number().int().min(1).max(28),
    default: 25,
  },
  'periods.label_by': {
    group: 'Reporting periods',
    label: 'Name periods after',
    description: 'end_month = 25 Aug → 24 Sep is called "September 2026"; start_month = "August 2026".',
    schema: z.enum(['end_month', 'start_month']),
    default: 'end_month',
  },
  'periods.auto_close_days': {
    group: 'Reporting periods',
    label: 'Close periods automatically after (days)',
    description:
      'Days after a period ends before it is closed automatically. 0 = never; an administrator closes it after reviewing. Closing freezes attendance for that period.',
    schema: z.number().int().min(0).max(60),
    default: 0,
  },
  'periods.notify_on_end': {
    group: 'Reporting periods',
    label: 'Notify administrators when a period ends',
    description: 'Sends a reminder to review and close the period.',
    schema: z.boolean(),
    default: true,
  },
  'payroll.default_pay_currency': {
    group: 'Payroll',
    label: 'Default pay currency',
    description: 'Currency salaries are paid in unless set differently on the employee (ISO code, e.g. EGP).',
    schema: z.string().trim().toUpperCase().regex(/^[A-Z]{3}$/, 'Use a 3-letter currency code, e.g. EGP'),
    default: 'USD',
  },
  'payroll.overtime_multiplier': {
    group: 'Payroll',
    label: 'Overtime multiplier',
    description: 'Hours paid above the required hours are paid at this multiple of the hourly rate (1.5 = time and a half).',
    schema: z.number().min(1).max(5),
    default: 1.5,
  },
  'payroll.hourly_rate_hours': {
    group: 'Payroll',
    label: 'Hours used to work out the hourly rate',
    description:
      'Hourly rate = monthly salary ÷ this number. 173.33 is the standard month (40 h × 52 weeks ÷ 12). 0 = divide by the hours required in that month instead.',
    schema: z.number().min(0).max(400),
    default: 173.33,
  },
  'payroll.shortfall': {
    group: 'Payroll',
    label: 'When required hours are not completed',
    description: 'percentage = paid the share of the salary for the hours completed (97 of 100 hours = 97%); deduct = missing hours are deducted at the normal hourly rate; none = the full salary is paid anyway.',
    schema: z.enum(['percentage', 'deduct', 'none']),
    default: 'percentage',
  },
  'payroll.paid_leave_counts': {
    group: 'Payroll',
    label: 'Paid leave counts as hours worked',
    description: 'Approved paid leave (e.g. annual vacation from the balance) adds that day\'s scheduled hours. Unpaid leave never counts.',
    schema: z.boolean(),
    default: true,
  },
  'payroll.rounding': {
    group: 'Payroll',
    label: 'Round amounts to',
    description: 'whole = whole currency units (e.g. 4,933); cents = two decimals.',
    schema: z.enum(['whole', 'cents']),
    default: 'whole',
  },
  'payroll.rate_source_url': {
    group: 'Payroll',
    label: 'Online exchange-rate source',
    description: 'Used by the refresh button on the Payroll page. {from} is replaced by the currency code. Must return JSON with a "rates" object.',
    schema: z.string().trim().url().startsWith('https://', 'Must be an https:// address'),
    default: 'https://open.er-api.com/v6/latest/{from}',
  },
  'productivity.enabled': {
    group: 'Productivity & privacy',
    label: 'Productivity tracking',
    description: 'Master switch. When off, the desktop agents stop recording and nothing new is stored.',
    schema: z.boolean(),
    default: true,
  },
  'productivity.track_when': {
    group: 'Productivity & privacy',
    label: 'Record activity only',
    description:
      'clocked_in = only while the person is clocked in (login → logout, recommended); schedule = only during their scheduled hours; always = whenever the computer is in use.',
    schema: z.enum(['clocked_in', 'schedule', 'always']),
    default: 'clocked_in',
  },
  'productivity.idle_minutes': {
    group: 'Productivity & privacy',
    label: 'Idle after (minutes)',
    description: 'No keyboard or mouse activity for this long counts as idle time.',
    schema: z.number().int().min(1).max(60),
    default: 5,
  },
  'productivity.capture_domains': {
    group: 'Productivity & privacy',
    label: 'Record website names',
    description: 'For browsers, record the website domain only (e.g. youtube.com) — never the full address, page content or searches.',
    schema: z.boolean(),
    default: true,
  },
  'productivity.capture_window_titles': {
    group: 'Productivity & privacy',
    label: 'Record window titles',
    description: 'Off by default. Window titles can contain file names or e-mail subjects; turn on only if needed and tell your staff.',
    schema: z.boolean(),
    default: false,
  },
  'productivity.unknown_counts_as': {
    group: 'Productivity & privacy',
    label: 'Apps and sites not yet classified count as',
    description: 'Until an administrator classifies them (Productivity → Not classified).',
    schema: z.enum(['neutral', 'productive', 'unproductive']),
    default: 'neutral',
  },
  'productivity.retention_days': {
    group: 'Productivity & privacy',
    label: 'Keep detailed activity for (days)',
    description: 'Detailed app/website records older than this are deleted automatically. Daily totals of closed reporting periods are kept.',
    schema: z.number().int().min(30).max(1095),
    default: 180,
  },
  'productivity.sync_minutes': {
    group: 'Productivity & privacy',
    label: 'Agent upload interval (minutes)',
    description: 'How often the desktop agent sends its data.',
    schema: z.number().int().min(1).max(60),
    default: 5,
  },
  'productivity.enrollment_code_hours': {
    group: 'Productivity & privacy',
    label: 'Connection codes are valid for (hours)',
    description: 'One-time code used to connect an employee\'s computer.',
    schema: z.number().int().min(1).max(720),
    default: 48,
  },
  'productivity.screenshots': {
    group: 'Productivity & privacy',
    label: 'Take screenshots',
    description:
      'When on, the desktop agent takes a few screenshots an hour during recorded time only. Turning this on or off changes the monitoring notice, so every employee must accept it again before anything is recorded.',
    schema: z.boolean(),
    default: false,
  },
  'productivity.screenshots_per_10min': {
    group: 'Productivity & privacy',
    label: 'Screenshots per 10 minutes',
    description: 'How many screenshots are taken in each 10-minute block, at random moments.',
    schema: z.number().int().min(1).max(6),
    default: 3,
  },
  'productivity.screenshot_retention_days': {
    group: 'Productivity & privacy',
    label: 'Keep screenshots for (days)',
    description: 'Screenshots older than this are deleted automatically.',
    schema: z.number().int().min(1).max(365),
    default: 60,
  },
  'productivity.screenshot_blur': {
    group: 'Productivity & privacy',
    label: 'Blur screenshots',
    description: 'Blur each screenshot so the program in use is visible but the details are not. Off by default.',
    schema: z.boolean(),
    default: false,
  },
  'productivity.flag_mouse_only': {
    group: 'Productivity & privacy',
    label: 'Flag suspicious activity',
    description:
      'Flag long stretches of mouse-only movement and the regular movement of “mouse jiggler” tools for review. Flagged time is shown for review, not deducted automatically. Counts only — never which keys are pressed or any text.',
    schema: z.boolean(),
    default: true,
  },
  'productivity.target_pct': {
    group: 'Productivity & privacy',
    label: 'Target productivity score (%)',
    description: 'Scores at or above this are shown in green; up to 20 points below in amber; lower in red. Reports flag people below target.',
    schema: z.number().int().min(1).max(100),
    default: 70,
  },
  'productivity.daily_report_notify': {
    group: 'Productivity & privacy',
    label: 'Send the daily productivity report notice',
    description: 'Each morning administrators get a notice with yesterday\'s team figures; managers get a link to their team\'s report.',
    schema: z.boolean(),
    default: true,
  },
  'productivity.daily_report_hour': {
    group: 'Productivity & privacy',
    label: 'Daily report notice time (hour, company time zone)',
    description: 'Hour of the day (0–23) when yesterday\'s report notice is sent.',
    schema: z.number().int().min(0).max(23),
    default: 9,
  },
  'productivity.notice_text': {
    group: 'Productivity & privacy',
    label: 'Notice shown to employees',
    description: 'Employees must accept this before their computer sends any data. Changing it asks everyone to accept again.',
    schema: z.string().trim().min(20).max(4000),
    default:
      'While you are clocked in, the company records which application is in use on this computer and, for web browsers, the website name (for example “autodesk.com”), and whether the computer is idle. This is used to prepare your daily report of productive, unproductive and idle time. No keystrokes or typed text are recorded, and nothing is recorded when you are clocked out. You can see all of your own data in the app under “My Productivity”.',
  },
  'productivity.notice_text_ar': {
    group: 'Productivity & privacy',
    label: 'Notice shown to employees (Arabic)',
    description: 'The same notice in Arabic, shown to people who use the app in Arabic. Changing it asks everyone to accept again.',
    schema: z.string().trim().min(20).max(4000),
    default:
      'أثناء تسجيل حضورك، تسجّل الشركة اسم التطبيق المستخدم على هذا الكمبيوتر، واسم الموقع في متصفحات الإنترنت (مثل «autodesk.com»)، وما إذا كان الكمبيوتر خاملًا. يُستخدم ذلك لإعداد تقريرك اليومي عن الوقت المنتج وغير المنتج ووقت الخمول. لا تُسجَّل ضغطات المفاتيح أو النصوص المكتوبة، ولا يُسجَّل أي شيء بعد تسجيل انصرافك. يمكنك الاطلاع على جميع بياناتك في التطبيق ضمن «إنتاجيتي».',
  },
  'leave.year_mode': {
    group: 'Leave',
    label: 'Leave year',
    description:
      'calendar = 1 January to 31 December. fixed_date = starts on the month/day below every year. anniversary = each employee\'s year starts on their employment start date. Changing this only affects leave years that have not been created yet.',
    schema: z.enum(['calendar', 'fixed_date', 'anniversary']),
    default: 'calendar',
  },
  'leave.year_start_month': {
    group: 'Leave',
    label: 'Leave year start month (fixed date)',
    description: 'Used when the leave year is "fixed_date". 1 = January.',
    schema: z.number().int().min(1).max(12),
    default: 1,
  },
  'leave.year_start_day': {
    group: 'Leave',
    label: 'Leave year start day (fixed date)',
    description: 'Used when the leave year is "fixed_date".',
    schema: z.number().int().min(1).max(28),
    default: 1,
  },
  'leave.tracking_start_date': {
    group: 'Leave',
    label: 'Leave tracking start date',
    description:
      'When this system started tracking leave. Unused days from leave years that began before this date are not carried over automatically — enter opening balances with an adjustment.',
    schema: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD'),
    default: '2000-01-01',
  },
  'leave.annual_first_year_days': {
    group: 'Leave',
    label: 'Annual leave in the first year of service (days)',
    description: 'Used by leave policies set to "Egyptian labour law". Egyptian law: 15 working days.',
    schema: z.number().min(0).max(366),
    default: 15,
  },
  'leave.annual_days': {
    group: 'Leave',
    label: 'Annual leave from the second year of service (days)',
    description: 'Egyptian law: 21 working days.',
    schema: z.number().min(0).max(366),
    default: 21,
  },
  'leave.annual_senior_days': {
    group: 'Leave',
    label: 'Annual leave after 10 years of experience or from age 50 (days)',
    description: 'Experience counts years with any employer (entered on the employee). Egyptian law: 30 working days.',
    schema: z.number().min(0).max(366),
    default: 30,
  },
  'leave.annual_disability_days': {
    group: 'Leave',
    label: 'Annual leave for employees with a disability (days)',
    description: 'Egyptian law: 45 working days.',
    schema: z.number().min(0).max(366),
    default: 45,
  },
  'leave.absence_uses_balance': {
    group: 'Leave',
    label: 'Absent days without approved leave',
    description:
      'annual_then_sick = paid from the annual leave balance, then from sick leave without a report; only when both are used up is the day deducted from the salary. annual = annual leave balance only. none = always deducted. Recorded as leave when the reporting period is closed.',
    schema: z.enum(['annual_then_sick', 'annual', 'none']),
    default: 'annual_then_sick',
  },
  'leave.prorate_first_year': {
    group: 'Leave',
    label: 'Prorate entitlement in the first year',
    description: 'Employees who join during a leave year get a share of the yearly entitlement (rounded to half days).',
    schema: z.boolean(),
    default: true,
  },
  'leave.max_backdate_days': {
    group: 'Leave',
    label: 'How far back employees may request leave (days)',
    description: 'For example sick leave recorded after the fact. Administrators can always record past leave.',
    schema: z.number().int().min(0).max(365),
    default: 30,
  },
  'leave.employee_can_cancel_approved': {
    group: 'Leave',
    label: 'Employees can cancel approved leave that has not started',
    description: 'If off, only administrators can cancel approved leave. Cancelling returns the days to the balance.',
    schema: z.boolean(),
    default: true,
  },
  'leave.notify_admins_on_submit': {
    group: 'Leave',
    label: 'Notify administrators of new requests',
    description: 'The employee\'s manager is always notified if they can approve leave.',
    schema: z.boolean(),
    default: true,
  },
  'holidays.official_sync': {
    group: 'Holidays',
    label: 'Official holiday calendars',
    description:
      'For calendars linked to an official source (e.g. the Egyptian Presidency page): off = never check; notify = check daily and tell administrators when the official list changes; auto = check daily and apply changes automatically.',
    schema: z.enum(['off', 'notify', 'auto']),
    default: 'notify',
  },
  'security.session_idle_minutes': {
    group: 'Security',
    label: 'Session idle timeout (minutes)',
    description: 'Users are signed out after this much inactivity.',
    schema: z.number().int().min(5).max(24 * 60),
    default: 60,
  },
  'security.session_absolute_hours': {
    group: 'Security',
    label: 'Maximum session length (hours)',
    description: 'Users must sign in again after this long, even if active.',
    schema: z.number().int().min(1).max(24 * 30),
    default: 12,
  },
  'security.max_failed_logins': {
    group: 'Security',
    label: 'Failed sign-ins before lockout',
    description: 'Account is temporarily locked after this many consecutive failed attempts.',
    schema: z.number().int().min(3).max(50),
    default: 5,
  },
  'security.lockout_minutes': {
    group: 'Security',
    label: 'Lockout duration (minutes)',
    description: 'How long a locked account stays locked.',
    schema: z.number().int().min(1).max(24 * 60),
    default: 15,
  },
  'security.password_min_length': {
    group: 'Security',
    label: 'Minimum password length',
    description: 'Applies to new and changed passwords.',
    schema: z.number().int().min(8).max(128),
    default: 10,
  },
} as const;

export type SettingKey = keyof typeof SETTING_DEFINITIONS;
export type SettingValue<K extends SettingKey> = z.infer<(typeof SETTING_DEFINITIONS)[K]['schema']>;
export type SettingsMap = { [K in SettingKey]: SettingValue<K> };

export const SETTING_KEYS = Object.keys(SETTING_DEFINITIONS) as SettingKey[];

export function isSettingKey(k: string): k is SettingKey {
  return Object.prototype.hasOwnProperty.call(SETTING_DEFINITIONS, k);
}

export function defaultSettings(): SettingsMap {
  const out: Record<string, unknown> = {};
  for (const k of SETTING_KEYS) out[k] = SETTING_DEFINITIONS[k].default;
  return out as SettingsMap;
}
